import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getProject, getAM, osOf, type Person } from "@/lib/mock-data";
import { getCliente, listarProjetos, useVersaoRegistry } from "@/api/registry";
import { isInactiveOn, usePeople } from "@/lib/people-store";
import {
  useCriarProfissional,
  useReordenarProfissionais,
  type NovoProfissional,
} from "@/api/profissionais";
import { ErroApi } from "@/api/client";
import type { DragEndEvent } from "@dnd-kit/core";
import { AlcaColuna, Arrastavel, AreaArrastavel, Soltavel } from "@/components/grade/arrastar";
import { idCelula, idColuna, idSlot, lerId } from "@/components/grade/ids";
import { PersonProfileDrawer } from "@/components/PersonProfileDrawer";
import { useParams, setParam } from "@/lib/params-store";
import {
  useAlloc,
  addDayAlloc,
  updateDayAlloc,
  removeDayAlloc,
  moveDayAlloc,
  shiftProjectFrom,
  duplicateDayAlloc,
  unidadeDoTipo,
  projetosNoDia,
  upsertRange,
  addDaysISO,
  currentMonday,
  garantirJanela,
  type DayAlloc,
} from "@/lib/alloc-store";
import { findBlock, useBlocks, useSolicitarBloqueio } from "@/lib/blocks-store";
import { useCriarSolicitacao } from "@/api/solicitacoes";
import {
  can,
  useRole,
  personalPersonId,
  isFreelancer,
  isGestor,
  isAdmin,
  isPlanejamento,
  isCoordenador,
} from "@/lib/use-role";
import { ProfissionalFormModal } from "@/components/ProfissionalFormModal";
import { GanttView } from "@/components/GanttView";
import { conflitosAtuais, avaliarPrevia, type SimSlot, type SimRange } from "@/lib/conflict-engine";
import { PreviaProjetoWizard } from "@/components/PreviaProjetoWizard";
import { coberturaEscopo, type CoberturaEscopo, type EtapaPlano } from "@/lib/plan-engine";
import {
  ZoomIn,
  ZoomOut,
  Lock,
  X,
  Search,
  UserPlus,
  GripVertical,
  Layers,
  Inbox,
  Trash2,
  Plus,
  ArrowLeftRight,
  MinusCircle,
  Wand2,
  AlertTriangle,
  Moon,
  Eye,
  LayoutGrid,
  GanttChartSquare,
  Check,
  ShieldAlert,
  Copy,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/loja")({
  validateSearch: (search: Record<string, unknown>): { previa?: string } =>
    typeof search.previa === "string" ? { previa: search.previa } : {},
  component: FlowGrid,
});

const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
/** A grade mostra a semana inteira: evento e ensaio acontecem no fim de semana. */
const DIAS_NA_SEMANA = 7;
const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
function fmt(dateISO: string) {
  const d = new Date(dateISO + "T00:00:00Z");
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MES[d.getUTCMonth()]}`;
}
function weekdayIdx(dateISO: string) {
  return (new Date(dateISO + "T00:00:00Z").getUTCDay() + 6) % 7;
}
function daysBetween(a: string, b: string) {
  return Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000);
}

function FlowGrid() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const role = useRole();
  const params = useParams();
  const alloc = useAlloc();
  const roster = usePeople();
  const blocks = useBlocks();
  const meId = personalPersonId(role);
  const podeGerir = isGestor(role) || isAdmin(role) || isPlanejamento(role);
  // Quem não tem `alocar` não arrasta: o servidor recusaria de qualquer forma.
  const podeAlocar = can(role, "alocar");

  const [zoom, setZoom] = useState(params.zoom);
  const [semanas, setSemanas] = useState(params.horizonteSemanas);
  const [grupo, setGrupo] = useState(false);
  const [novo, setNovo] = useState<{ personId: string; date: string } | null>(null);
  const [detalhe, setDetalhe] = useState<DayAlloc | null>(null);
  const [duplicar, setDuplicar] = useState<DayAlloc | null>(null);
  const [bloqueio, setBloqueio] = useState<Person | null>(null);
  const [novoProfissional, setNovoProfissional] = useState(false);
  const [perfilAberto, setPerfilAberto] = useState<string | null>(null);

  const criarProfissional = useCriarProfissional();
  const reordenar = useReordenarProfissionais();
  const [view, setView] = useState<"grade" | "gantt">("grade");
  const [sims, setSims] = useState<SimSlot[]>([]);
  const [simRanges, setSimRanges] = useState<SimRange[]>([]);
  const [cobertura, setCobertura] = useState<CoberturaEscopo[] | null>(null);
  const [wizard, setWizard] = useState<{ projectId?: string } | null>(
    search.previa ? { projectId: search.previa } : null,
  );

  // RF-14 — a pausa mora no projeto, no servidor. Antes era um override em
  // localStorage, que só o próprio navegador enxergava.
  const pausado = (projectId: string) => getProject(projectId)?.stage === "Pausado";

  const conflitosGrade = useMemo(() => conflitosAtuais(alloc, blocks), [alloc, blocks]);
  const previa = useMemo(
    () => (sims.length || simRanges.length ? avaliarPrevia(alloc, sims, blocks, simRanges) : null),
    [alloc, sims, blocks, simRanges],
  );

  const limparPrevia = () => {
    setSims([]);
    setSimRanges([]);
    setCobertura(null);
  };

  const fecharWizard = () => {
    setWizard(null);
    if (search.previa) navigate({ search: () => ({}), replace: true });
  };

  const confirmarPrevia = async () => {
    const total = sims.length + simRanges.length;
    let feitos = 0;
    // Quem ainda não tem vínculo com o projeto: o portão do M9 recusa com 409.
    const semVinculo = new Set<string>();
    const outrosErros: string[] = [];
    const registrar = (erro: unknown, personId: string) => {
      if (erro instanceof ErroApi && erro.status === 409) semVinculo.add(personId);
      else outrosErros.push(erro instanceof Error ? erro.message : String(erro));
    };

    for (const s of sims) {
      const { simId: _simId, ...rest } = s;
      try {
        await addDayAlloc(rest);
        feitos++;
      } catch (erro) {
        registrar(erro, s.personId);
      }
    }
    for (const r of simRanges) {
      const { simId: _simId, ...rest } = r;
      try {
        await upsertRange(rest);
        feitos++;
      } catch (erro) {
        registrar(erro, r.personId);
      }
    }

    if (feitos === total) {
      toast.success(`${feitos} alocação(ões) confirmada(s)`);
    } else {
      const nomes = [...semVinculo].map((id) => roster.find((p) => p.id === id)?.nome ?? id);
      toast.error(`${feitos} de ${total} alocações criadas`, {
        description: [
          nomes.length &&
            `${nomes.join(", ")} ainda não ${nomes.length > 1 ? "estão" : "está"} no projeto: o primeiro vínculo passa por solicitação e aceite — peça pela célula da grade.`,
          outrosErros[0],
        ]
          .filter(Boolean)
          .join(" "),
        duration: 12_000,
      });
    }
    limparPrevia();
  };

  // A grade nascia presa na semana corrente. A SOAP planeja com meses de
  // antecedência e revisita o que passou, então a âncora é navegável.
  const [monday, setMonday] = useState(currentMonday);
  const hoje = currentMonday();

  const dates = useMemo(() => {
    const out: string[] = [];
    for (let w = 0; w < semanas; w++)
      for (let d = 0; d < DIAS_NA_SEMANA; d++) out.push(addDaysISO(monday, w * 7 + d));
    return out;
  }, [monday, semanas]);

  // Sair da janela buscada desenharia células vazias com cara de agenda livre.
  useEffect(() => {
    garantirJanela(dates[0], dates[dates.length - 1]);
  }, [dates]);

  /** Segunda-feira da semana de uma data qualquer — o seletor sempre encaixa. */
  const irPara = (iso: string) => {
    if (!iso) return;
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    setMonday(d.toISOString().slice(0, 10));
  };

  const columns = useMemo(() => {
    // O roster já vem ordenado por `ordemExibicao`, persistida no servidor.
    // Roteiristas saem da grade: são geridos por período na visão Gantt.
    const prod = roster.filter((p) =>
      ["Designer", "Diretor de Arte e Designer", "Freelancer"].includes(p.funcao),
    );
    return isFreelancer(role) && meId ? prod.filter((p) => p.id === meId) : prod;
  }, [roster, role, meId]);

  const colW = Math.round(190 * zoom);
  const rowH = Math.round(96 * zoom);

  const corDoProjeto = (projectId: string) => {
    const p = getProject(projectId);
    if (!p) return params.corOrcamento;
    if (p.stage === "Em orçamento") return params.corOrcamento;
    return params.coresAtendimento[p.amId] ?? getAM(p.amId)?.cor ?? params.corOrcamento;
  };

  /**
   * Um `onDragEnd` para os dois arrastes da tela. O prefixo do id diz qual é:
   * `coluna:` reordena profissionais, `slot:` move alocação para `celula:`.
   */
  const aoSoltar = (evento: DragEndEvent) => {
    const origem = lerId(String(evento.active.id));
    if (!evento.over) return;
    const destino = lerId(String(evento.over.id));

    if (origem.tipo === "coluna" && destino.tipo === "coluna") {
      reorder(origem.valor!, destino.valor!);
      return;
    }
    if (origem.tipo === "slot" && destino.tipo === "celula") {
      void moverSlot(origem.valor!, destino.profissionalId!, destino.data!);
    }
  };

  const moverSlot = async (slotId: string, personId: string, date: string) => {
    const a = alloc.days.find((d) => d.id === slotId);
    if (!a) return;
    if (a.personId === personId && a.date === date) return;

    try {
      if (grupo) {
        const delta = daysBetween(a.date, date);
        if (delta === 0) return;
        // Desloca este slot e os seguintes; o que já passou fica no lugar.
        const r = await shiftProjectFrom(a.projectId, a.id, a.date, a.date, delta);
        toast.success(
          `${r.slots} slot(s) reprogramado(s) em ${delta > 0 ? "+" : ""}${delta} dias`,
          { description: r.aviso?.mensagem },
        );
        return;
      }
      const aviso = await moveDayAlloc(a.id, personId, date, a.date);
      toast.success("Slot reprogramado", { description: aviso?.mensagem });
    } catch (e) {
      // 409 quando outra pessoa já moveu o slot — o servidor arbitra.
      toast.error(e instanceof Error ? e.message : "Não foi possível reprogramar");
    }
  };

  const reorder = (arrastada: string, alvo: string) => {
    if (arrastada === alvo) return;
    const cur = columns.map((c) => c.id);
    const from = cur.indexOf(arrastada);
    const to = cur.indexOf(alvo);
    if (from < 0 || to < 0) return;
    cur.splice(to, 0, ...cur.splice(from, 1));
    reordenar.mutate(cur);
  };

  return (
    <>
      <TopBar
        title="Flow"
        subtitle="Grade de alocação — dias nas linhas, profissionais nas colunas · alterne para Gantt no seletor de visão"
      />

      {/*
        A página inteira rolava: não havia contêiner de scroll nenhum, então o
        `sticky top-0` do cabeçalho grudava no topo da **viewport**, atrás da
        TopBar (z-30 contra z-20), e os nomes dos profissionais sumiam ao
        descer. Fixar a altura aqui faz da grade o único trecho que rola — a
        TopBar, a barra de ferramentas e a linha de nomes ficam paradas.
      */}
      <div className="flex h-[calc(100dvh-3.5rem)] flex-col">
        {/* Toolbar */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-card px-4 py-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Visão
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(
              [
                ["grade", "Grade", LayoutGrid],
                ["gantt", "Gantt", GanttChartSquare],
              ] as const
            ).map(([v, label, Icon]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium",
                  view === v
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {/*
          Navegação de período. O passo é de uma semana porque a grade é
          semanal; o seletor de data encaixa em qualquer segunda-feira, que é
          como se chega a janeiro sem clicar doze vezes.
        */}
          <div className="flex items-center gap-1 rounded-md border border-border px-1">
            <button
              title="Semana anterior"
              onClick={() => setMonday((m) => addDaysISO(m, -7))}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <input
              type="date"
              aria-label="Ir para a semana de"
              value={monday}
              onChange={(e) => irPara(e.target.value)}
              className="h-7 w-[8.5rem] rounded-md border-0 bg-transparent px-1 text-xs text-foreground outline-none"
            />
            <button
              title="Próxima semana"
              onClick={() => setMonday((m) => addDaysISO(m, 7))}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setMonday(hoje)}
              disabled={monday === hoje}
              className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              Hoje
            </button>
          </div>

          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            Horizonte
            <select
              value={semanas}
              onChange={(e) => {
                const v = Number(e.target.value);
                setSemanas(v);
                setParam("horizonteSemanas", v);
              }}
              className="h-7 rounded-md border border-border bg-background px-2 text-xs text-foreground"
            >
              {[2, 4, 6, 8, 12].map((n) => (
                <option key={n} value={n}>
                  {n} semanas
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1 rounded-md border border-border px-1">
            <button
              onClick={() => {
                const v = Math.max(0.6, +(zoom - 0.1).toFixed(2));
                setZoom(v);
                setParam("zoom", v);
              }}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <span className="w-10 text-center text-[11px] text-muted-foreground">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => {
                const v = Math.min(1.6, +(zoom + 0.1).toFixed(2));
                setZoom(v);
                setParam("zoom", v);
              }}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
          </div>

          <button
            onClick={() => setGrupo(!grupo)}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]",
              grupo
                ? "border-primary bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            <Layers className="h-3.5 w-3.5" />{" "}
            {grupo ? "Arrastando projeto a partir do slot" : "Arrastar slot único"}
          </button>

          <button
            onClick={() => setWizard({})}
            className="flex items-center gap-1.5 rounded-md border border-primary bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary hover:bg-primary/20"
          >
            <Wand2 className="h-3.5 w-3.5" /> Simular projeto
          </button>

          <Link
            to="/solicitacoes"
            className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <Inbox className="h-3.5 w-3.5" /> Fila de solicitações
          </Link>

          {podeGerir && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1.5 text-[11px]"
              onClick={() => setNovoProfissional(true)}
            >
              <UserPlus className="h-3.5 w-3.5" /> Novo profissional
            </Button>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {Object.entries(params.coresAtendimento).map(([amId, cor]) => (
              <span
                key={amId}
                className="flex items-center gap-1 text-[10px] text-muted-foreground"
              >
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: cor }} />
                {getAM(amId)?.nome ?? amId}
              </span>
            ))}
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: params.corOrcamento }}
              />{" "}
              Orçamento
            </span>
          </div>
        </div>

        {view === "gantt" && <GanttView semanas={semanas} inicio={monday} />}

        {/* Grade */}
        <AreaArrastavel aoSoltar={aoSoltar}>
          <div className={cn("min-h-0 flex-1 overflow-auto", view === "gantt" && "hidden")}>
            <div style={{ minWidth: 110 + columns.length * colW }}>
              {/* header */}
              <div className="sticky top-0 z-20 flex border-b border-border bg-card">
                <div className="w-[110px] shrink-0 border-r border-border px-2 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Dia
                </div>
                {columns.map((p) => {
                  const ativos = alloc.ranges.filter((r) => r.personId === p.id).length;
                  return (
                    <Soltavel
                      key={p.id}
                      id={idColuna(p.id)}
                      style={{ width: colW }}
                      className="shrink-0 border-r border-border px-2 py-2"
                    >
                      <div className="flex items-center gap-1">
                        {/* A alça é só o grip: o nome continua abrindo o perfil. */}
                        <AlcaColuna id={idColuna(p.id)} className="text-muted-foreground">
                          <GripVertical className="h-3 w-3" />
                        </AlcaColuna>
                        <button
                          onClick={() => setPerfilAberto(p.id)}
                          title="Abrir perfil do profissional"
                          className="truncate text-left text-xs font-semibold text-foreground hover:text-primary hover:underline"
                        >
                          {p.nome}
                        </button>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-1">
                        <span className="truncate text-[10px] text-muted-foreground">
                          {p.funcao === "Roteirista"
                            ? `${ativos} jobs · teto ${params.tetoRoteirista}`
                            : `${p.tipo} · ${params.slidesPorDia} sl/dia`}
                        </span>
                        <button
                          title="Bloquear agenda"
                          onClick={() => setBloqueio(p)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Lock className="h-3 w-3" />
                        </button>
                      </div>
                      {p.clientTags?.length ? (
                        <div className="mt-1 flex flex-wrap gap-0.5">
                          {p.clientTags.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="rounded-sm bg-secondary px-1 text-[9px] text-muted-foreground"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </Soltavel>
                  );
                })}
              </div>

              {/* linhas */}
              {dates.map((date, i) => {
                const novaSemana = i % DIAS_NA_SEMANA === 0;
                return (
                  <div key={date}>
                    {novaSemana && (
                      <div className="sticky left-0 flex items-center gap-2 border-y border-border bg-secondary/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Semana {i / DIAS_NA_SEMANA + 1} · {fmt(date)} –{" "}
                        {fmt(addDaysISO(date, DIAS_NA_SEMANA - 1))}
                      </div>
                    )}
                    <div className="flex border-b border-border/60">
                      <div
                        className="w-[110px] shrink-0 border-r border-border px-2 py-1.5"
                        style={{ minHeight: rowH }}
                      >
                        <div className="text-xs font-medium text-foreground">
                          {WD[weekdayIdx(date)]}
                        </div>
                        <div className="text-[10px] text-muted-foreground">{fmt(date)}</div>
                      </div>

                      {columns.map((p) => {
                        const bloqueado = findBlock(blocks, p.id, date);
                        const inativo = isInactiveOn(p, date);
                        const isRoteirista = p.funcao === "Roteirista";
                        // Projeto pausado sai do Flow até ser retomado com nova data.
                        const slots = alloc.days.filter(
                          (d) => d.personId === p.id && d.date === date && !pausado(d.projectId),
                        );
                        const faixas = isRoteirista
                          ? alloc.ranges
                              .filter((r) => r.personId === p.id)
                              .sort(
                                (a, b) =>
                                  a.inicio.localeCompare(b.inicio) || a.id.localeCompare(b.id),
                              )
                          : [];
                        const barras = faixas.filter((r) => date >= r.inicio && date <= r.fim);
                        // RF-62 — o teto conta OS distintas, não slots: dois slots da
                        // mesma OS no mesmo dia (IV de manhã, Slide à tarde) são o caso
                        // normal. Quem calcula é o servidor, para que a grade e o motor
                        // de cronograma nunca discordem sobre capacidade.
                        const sobrecargaCelula = alloc.sobrecargas.find(
                          (s) => s.personId === p.id && s.date === date,
                        );
                        const projetosDia = sobrecargaCelula?.osDistintas ?? 0;
                        const excesso = Boolean(sobrecargaCelula);
                        const sobrecarga = isRoteirista && barras.length > params.tetoRoteirista;
                        const simulados = sims.filter(
                          (s) => s.personId === p.id && s.date === date,
                        );
                        const conflitoCelula =
                          previa?.conflitos.filter((c) => c.personId === p.id && c.date === date) ??
                          [];

                        return (
                          <Soltavel
                            key={p.id}
                            id={idCelula(p.id, date)}
                            data-celula={`${p.id}|${date}`}
                            style={{ width: colW, minHeight: rowH }}
                            onClick={() => {
                              if (!bloqueado && !inativo) setNovo({ personId: p.id, date });
                            }}
                            className={cn(
                              "shrink-0 cursor-pointer border-r border-border transition-colors hover:bg-secondary/30",
                              isRoteirista ? "relative px-0.5" : "space-y-1 p-1",
                              bloqueado && "bg-secondary/60",
                              inativo && "cursor-not-allowed bg-muted/50",
                              conflitoCelula.length && "bg-health-red/5",
                              excesso && "border-2 border-health-red bg-health-red/10",
                              sobrecarga && "ring-2 ring-inset ring-health-red",
                            )}
                            title={
                              excesso
                                ? `Sobrecarga: ${projetosDia} projetos distintos no dia (teto ${alloc.teto})`
                                : undefined
                            }
                          >
                            {excesso && (
                              <div className="flex items-center gap-1 rounded-sm bg-health-red/20 px-1 text-[9px] font-semibold text-health-red">
                                <AlertTriangle className="h-3 w-3" /> {projetosDia} projetos no dia
                              </div>
                            )}
                            {inativo && (
                              <div className="flex items-center gap-1 rounded-sm border border-dashed border-muted-foreground/40 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
                                <Moon className="h-3 w-3" /> indisponível
                              </div>
                            )}
                            {bloqueado && (
                              <div
                                className={cn(
                                  "flex items-start gap-1 rounded-md border px-1.5 py-1 text-[10px]",
                                  bloqueado.status === "PENDENTE"
                                    ? "border-dashed border-health-yellow bg-health-yellow/10 text-health-yellow"
                                    : bloqueado.status === "APROVADO"
                                      ? "border-solid border-health-green bg-health-green/10 text-health-green"
                                      : "border-border text-muted-foreground line-through",
                                )}
                              >
                                {bloqueado.status === "PENDENTE" ? (
                                  <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                                ) : bloqueado.status === "APROVADO" ? (
                                  <Check className="mt-0.5 h-3 w-3 shrink-0" />
                                ) : (
                                  <X className="mt-0.5 h-3 w-3 shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <div className="font-semibold">
                                    {bloqueado.tipo ?? "Bloqueio"} ·{" "}
                                    {bloqueado.status === "PENDENTE"
                                      ? "TRAVADO"
                                      : bloqueado.status === "APROVADO"
                                        ? "APROVADO"
                                        : "RECUSADO"}
                                  </div>
                                  <div className="truncate">
                                    {bloqueado.motivo ??
                                      (bloqueado.status === "PENDENTE"
                                        ? "aguardando aprovação"
                                        : "")}
                                  </div>
                                </div>
                              </div>
                            )}

                            {isRoteirista ? (
                              <>
                                {sobrecarga && (
                                  <span
                                    className="absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-sm bg-health-red/15 px-1 text-[9px] font-semibold text-health-red"
                                    title={`Sobrecarga: ${barras.length} projetos simultâneos (teto ${params.tetoRoteirista})`}
                                  >
                                    <AlertTriangle className="h-3 w-3" /> {barras.length}/
                                    {params.tetoRoteirista}
                                  </span>
                                )}
                                <div
                                  className="flex items-stretch gap-[2px]"
                                  style={{ minHeight: rowH }}
                                >
                                  {faixas.length === 0 && <div className="flex-1" />}
                                  {faixas.map((r) => {
                                    const ativa = date >= r.inicio && date <= r.fim;
                                    if (!ativa) return <div key={r.id} className="flex-1" />;
                                    const cor = corDoProjeto(r.projectId);
                                    const primeiro =
                                      r.inicio === date || (dates[0] === date && r.inicio < date);
                                    const ultimo = r.fim === date;
                                    return (
                                      <div
                                        key={r.id}
                                        onClick={(e) => e.stopPropagation()}
                                        title={`${osOf(r.projectId)} · ${getProject(r.projectId)?.cliente} · ${r.horas}h · ${r.inicio} → ${r.fim}`}
                                        className={cn(
                                          "relative flex-1",
                                          !primeiro && "border-t border-dashed border-border/40",
                                        )}
                                        style={{
                                          background: `${cor}26`,
                                          borderLeft: `3px solid ${cor}`,
                                          borderRadius: `${primeiro ? 4 : 0}px ${primeiro ? 4 : 0}px ${ultimo ? 4 : 0}px ${ultimo ? 4 : 0}px`,
                                        }}
                                      >
                                        {primeiro && (
                                          <div className="pointer-events-none absolute inset-x-0 top-0 px-1 pt-0.5 text-[9px] leading-tight text-foreground">
                                            <div className="truncate font-semibold">
                                              {getProject(r.projectId)?.cliente}
                                            </div>
                                            <div className="text-muted-foreground">{r.horas}h</div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            ) : (
                              slots.map((s) => {
                                const conf = conflitosGrade.get(s.id);
                                return (
                                  <Arrastavel
                                    key={s.id}
                                    id={idSlot(s.id)}
                                    data-slot={s.id}
                                    desabilitado={!podeAlocar}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDetalhe(s);
                                    }}
                                    title={conf?.motivo}
                                    className={cn(
                                      "rounded-md bg-card px-1.5 py-1 text-[10px] shadow-sm",
                                      conf && "ring-2 ring-offset-1 ring-offset-background",
                                      conf?.severidade === "alto" && "ring-health-red",
                                      conf?.severidade === "medio" && "ring-health-yellow",
                                    )}
                                    style={{ border: `2px solid ${corDoProjeto(s.projectId)}` }}
                                  >
                                    <div className="flex items-center justify-between gap-1">
                                      <span className="flex min-w-0 items-center gap-1">
                                        {conf && (
                                          <AlertTriangle
                                            className={cn(
                                              "h-3 w-3 shrink-0",
                                              conf.severidade === "alto"
                                                ? "text-health-red"
                                                : "text-health-yellow",
                                            )}
                                          />
                                        )}
                                        <span className="truncate font-semibold text-foreground">
                                          {getProject(s.projectId)?.cliente}
                                        </span>
                                      </span>
                                      <span className="flex shrink-0 items-center gap-1">
                                        {s.tipo === "Alteração" && (
                                          <span className="rounded-sm bg-primary px-1 text-[9px] font-black tracking-wider text-primary-foreground">
                                            ALT
                                          </span>
                                        )}
                                        <span className="text-muted-foreground">
                                          {s.qtd} {s.unidade === "slides" ? "sl" : "h"}
                                        </span>
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-1">
                                      <span className="truncate text-muted-foreground">
                                        {osOf(s.projectId)} · {s.tipo}
                                      </span>
                                      <button
                                        title="Duplicar slot"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDuplicar(s);
                                        }}
                                        className="shrink-0 text-muted-foreground hover:text-primary"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </Arrastavel>
                                );
                              })
                            )}

                            {simulados.map((s) => (
                              <div
                                key={s.simId}
                                onClick={(e) => e.stopPropagation()}
                                className={cn(
                                  "rounded-md border-2 border-dashed bg-muted/40 px-1.5 py-1 text-[10px] text-muted-foreground",
                                  conflitoCelula.length
                                    ? "border-health-red"
                                    : "border-muted-foreground/60",
                                )}
                                title={
                                  conflitoCelula.map((c) => c.motivo).join(" · ") ||
                                  "Prévia — ainda não confirmada"
                                }
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <span className="flex min-w-0 items-center gap-1">
                                    {conflitoCelula.length ? (
                                      <AlertTriangle className="h-3 w-3 shrink-0 text-health-red" />
                                    ) : (
                                      <Eye className="h-3 w-3 shrink-0" />
                                    )}
                                    <span className="truncate font-semibold">
                                      {getProject(s.projectId)?.cliente}
                                    </span>
                                  </span>
                                  <span className="shrink-0">
                                    {s.qtd} {s.unidade === "slides" ? "sl" : "h"}
                                  </span>
                                </div>
                                <div className="truncate">prévia · {s.tipo}</div>
                              </div>
                            ))}
                          </Soltavel>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </AreaArrastavel>
      </div>

      {previa && (
        <PreviaPanel
          previa={previa}
          sims={sims}
          simRanges={simRanges}
          cobertura={cobertura}
          roster={roster}
          onConfirmar={confirmarPrevia}
          onAjustar={() => {
            const s = sims[0];
            const projectId = s?.projectId ?? simRanges[0]?.projectId;
            limparPrevia();
            if (projectId) setWizard({ projectId });
            else if (s) setNovo({ personId: s.personId, date: s.date });
          }}
          onDescartar={() => {
            limparPrevia();
            toast("Prévia descartada");
          }}
        />
      )}

      {wizard && (
        <PreviaProjetoWizard
          projectId={wizard.projectId}
          onClose={fecharWizard}
          onGerar={(novosSims, novosRanges, _project, etapas: EtapaPlano[]) => {
            setSims(novosSims);
            setSimRanges(novosRanges);
            setCobertura(coberturaEscopo(etapas, novosSims, novosRanges));
            fecharWizard();
            toast("Prévia gerada a partir do escopo do projeto — revise o impacto");
          }}
        />
      )}

      {novo && (
        <NovaAlocacao
          personId={novo.personId}
          date={novo.date}
          onClose={() => setNovo(null)}
          onPreview={(s) => {
            setSims((prev) => [...prev, { ...s, simId: `sim_${Date.now()}_${prev.length}` }]);
            setNovo(null);
            toast("Prévia gerada — revise o impacto antes de confirmar");
          }}
        />
      )}
      {detalhe && <DetalheSlot slot={detalhe} onClose={() => setDetalhe(null)} />}
      {duplicar && <DuplicarSlotModal slot={duplicar} onClose={() => setDuplicar(null)} />}
      {bloqueio && (
        <BloqueioModal person={bloqueio} podeGerir={podeGerir} onClose={() => setBloqueio(null)} />
      )}
      {novoProfissional && (
        <ProfissionalFormModal
          initial={null}
          salvando={criarProfissional.isPending}
          onClose={() => setNovoProfissional(false)}
          onSave={(dados: NovoProfissional) =>
            criarProfissional.mutate(dados, {
              onSuccess: () => {
                setNovoProfissional(false);
                toast.success("Profissional cadastrado");
              },
              onError: (e) => toast.error(e instanceof Error ? e.message : "Falha ao cadastrar"),
            })
          }
        />
      )}
      {perfilAberto && (
        <PersonProfileDrawer
          profissionalId={perfilAberto}
          readOnly={!podeGerir}
          onClose={() => setPerfilAberto(null)}
        />
      )}
      {isCoordenador(role) && (
        <div className="pointer-events-none fixed bottom-4 right-4 z-40 rounded-md border border-border bg-card px-3 py-2 text-[10px] text-muted-foreground shadow-lg">
          Clique em um bloco para pedir Trocar / Retirar / Adicionar
        </div>
      )}
    </>
  );
}

/* ---------- Painel de prévia ---------- */
function PreviaPanel({
  previa,
  sims,
  simRanges,
  cobertura,
  roster,
  onConfirmar,
  onAjustar,
  onDescartar,
}: {
  previa: NonNullable<ReturnType<typeof avaliarPrevia>>;
  sims: SimSlot[];
  simRanges: SimRange[];
  cobertura: CoberturaEscopo[] | null;
  roster: Person[];
  onConfirmar: () => void;
  onAjustar: () => void;
  onDescartar: () => void;
}) {
  const nome = (id: string) => roster.find((p) => p.id === id)?.nome ?? id;
  const altos = previa.conflitos.filter((c) => c.severidade === "alto");

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 max-h-[45vh] overflow-auto border-t-2 border-dashed border-primary bg-card/95 p-3 shadow-2xl backdrop-blur">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-xs font-semibold text-foreground">
          <Eye className="h-3.5 w-3.5 text-primary" /> Prévia de alocação — {sims.length} slot(s)
          simulado(s)
          {simRanges.length > 0 && ` + ${simRanges.length} período(s) de conteúdo`}
        </span>
        {altos.length > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-health-red/15 px-2 py-0.5 text-[10px] font-semibold text-health-red">
            <ShieldAlert className="h-3 w-3" /> {altos.length} conflito(s) crítico(s)
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            className="h-8 gap-1 text-xs"
            onClick={onConfirmar}
            disabled={altos.length > 0}
          >
            <Check className="h-3.5 w-3.5" /> Confirmar
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onAjustar}>
            Ajustar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1 text-xs text-health-red"
            onClick={onDescartar}
          >
            <Trash2 className="h-3.5 w-3.5" /> Descartar
          </Button>
        </div>
      </div>

      {altos.length > 0 && (
        <p className="mb-2 text-[10px] text-health-red">
          Resolva os conflitos críticos para poder confirmar.
        </p>
      )}

      {cobertura && cobertura.length > 0 && (
        <div className="mb-2 rounded-lg border border-border p-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Cobertura do escopo
          </div>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {cobertura.map((c) => {
              const total = c.jaAlocado + c.simulado;
              const pct = c.escopo ? Math.min(100, Math.round((total / c.escopo) * 100)) : 0;
              return (
                <div key={c.etapa} className="text-[11px]">
                  <div className="flex justify-between text-muted-foreground">
                    <span className="text-foreground">{c.etapa}</span>
                    <span>
                      {total}/{c.escopo} {c.unidade === "horas" ? "h" : "slides"} · {pct}%
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-2 md:grid-cols-2">
        <div className="rounded-lg border border-border p-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Impacto por profissional / semana
          </div>
          {previa.resumos.map((r) => (
            <div
              key={`${r.personId}${r.semanaInicio}`}
              className="border-b border-border/60 py-1 last:border-0"
            >
              <div className="text-[11px] font-semibold text-foreground">
                {nome(r.personId)} · semana de {fmt(r.semanaInicio)}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Slides {r.slidesAntes} → <span className="text-foreground">{r.slidesDepois}</span> ·
                Horas {r.horasAntes} → <span className="text-foreground">{r.horasDepois}</span> ·
                Projetos simultâneos {r.projetosSemanaAntes} →{" "}
                <span className="text-foreground">{r.projetosSemanaDepois}</span> · Pico de{" "}
                {r.maxProjetosDia} projeto(s)/dia
              </div>
              <div className="text-[10px] text-muted-foreground">
                Dias afetados: {r.diasAfetados.map(fmt).join(", ")}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border p-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Conflitos gerados
          </div>
          {previa.conflitos.length ? (
            previa.conflitos.map((c) => (
              <div key={c.key} className="flex items-start gap-1 py-0.5">
                <AlertTriangle
                  className={cn(
                    "mt-0.5 h-3 w-3 shrink-0",
                    c.severidade === "alto" ? "text-health-red" : "text-health-yellow",
                  )}
                />
                <span className="text-[11px] text-muted-foreground">
                  <span className="text-foreground">
                    {nome(c.personId)} · {fmt(c.date)}
                  </span>{" "}
                  — {c.motivo}
                </span>
              </div>
            ))
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Nenhum conflito detectado nesta simulação.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Duplicação de slot ---------- */
function DuplicarSlotModal({ slot, onClose }: { slot: DayAlloc; onClose: () => void }) {
  const [date, setDate] = useState(slot.date);
  const [qtd, setQtd] = useState(slot.qtd);
  const rotulo = slot.unidade === "slides" ? "Slides previstos" : "Horas previstas";

  const confirmar = () => {
    const id = duplicateDayAlloc(slot.id, { date, qtd });
    if (!id) {
      toast.error("Não foi possível duplicar o slot");
      return;
    }
    toast.success("Slot duplicado");
    onClose();
  };

  return (
    <Overlay onClose={onClose} title={`Duplicar slot · ${osOf(slot.projectId)}`}>
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Revise a volumetria antes de concluir — o novo slot mantém o tipo{" "}
          <strong className="text-foreground">{slot.tipo}</strong> e a unidade{" "}
          <strong className="text-foreground">{slot.unidade}</strong>.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Dia">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inp}
            />
          </Campo>
          <Campo label={rotulo}>
            <input
              type="number"
              value={qtd}
              onChange={(e) => setQtd(Number(e.target.value) || 0)}
              className={inp}
            />
          </Campo>
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" className="h-8 gap-1 text-xs" onClick={confirmar}>
            <Copy className="h-3.5 w-3.5" /> Duplicar
          </Button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------- Nova alocação ---------- */
function NovaAlocacao({
  personId,
  date,
  onClose,
  onPreview,
}: {
  personId: string;
  date: string;
  onClose: () => void;
  onPreview: (s: Omit<DayAlloc, "id">) => void;
}) {
  const params = useParams();
  const roster = usePeople();
  const person = roster.find((p) => p.id === personId);
  const roteirista = person?.funcao === "Roteirista";
  const [q, setQ] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [tipo, setTipo] = useState(params.slotTipos[0] ?? "Slide");
  const [unidade, setUnidade] = useState<"slides" | "horas">(roteirista ? "horas" : "slides");
  const [qtd, setQtd] = useState(params.slidesPorDia);
  const [fim, setFim] = useState(addDaysISO(date, 9));
  // Preenchido quando o servidor recusa a gravação direta: o primeiro vínculo
  // de alguém num projeto passa pelo M9 (RF-53).
  const [justificativa, setJustificativa] = useState<string | null>(null);
  const criarSolicitacao = useCriarSolicitacao();

  // Projetos do banco, não do mock: até o B-35 a lista oferecia os vinte
  // projetos fictícios e nunca o card recém-criado. Fechado e pausado saem —
  // pausa tira o projeto do fluxo ativo (RF-14).
  useVersaoRegistry();
  const ativos = listarProjetos()
    .filter((p) => !p.encerrado && !p.pausado)
    .sort((a, b) => a.os.localeCompare(b.os, "pt-BR"));
  const lista = ativos.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      p.os.toLowerCase().includes(s) ||
      (getCliente(p.clienteId)?.nome ?? "").toLowerCase().includes(s) ||
      p.nome.toLowerCase().includes(s)
    );
  });

  const escolherTipo = (t: string) => {
    setTipo(t);
    const u = unidadeDoTipo(t);
    setUnidade(u);
    if (t === "IV" || t === "Adaptação de IV") setQtd(params.slidesIVPorDia);
    else if (t === "Alteração") setQtd(params.horasPorDia);
    else setQtd(u === "slides" ? params.slidesPorDia : params.horasPorDia);
  };

  const salvar = async () => {
    if (!projectId) return;
    try {
      if (roteirista) {
        await upsertRange({ personId, projectId, inicio: date, fim, horas: qtd });
        toast.success("Período de conteúdo alocado");
      } else {
        await addDayAlloc({ personId, date, projectId, tipo, unidade, qtd });
        toast.success("Alocação criada");
      }
      onClose();
    } catch (erro) {
      // 409 = ainda não há vínculo aceito. Vira solicitação, não erro seco.
      if (erro instanceof ErroApi && erro.status === 409) {
        setJustificativa("");
        toast.message("Primeiro vínculo neste projeto", {
          description: "Precisa passar pelo Planejamento e pelo aceite do profissional.",
        });
        return;
      }
      toast.error(erro instanceof Error ? erro.message : "Não foi possível alocar");
    }
  };

  const pedirAlocacao = () => {
    if (!projectId) return;
    if ((justificativa ?? "").trim().length < 10) {
      toast.error("Descreva o porquê — o Planejamento decide com base nisso");
      return;
    }
    criarSolicitacao.mutate(
      {
        tipo: "ADICIONAR",
        projetoId: projectId,
        profissionalId: personId,
        inicio: date,
        ...(roteirista
          ? { ehPeriodo: true, fim, unidade: "HORAS" as const, quantidade: qtd }
          : {
              tipoSlot: tipo,
              unidade: unidade === "horas" ? ("HORAS" as const) : ("SLIDES" as const),
              quantidade: qtd,
            }),
        justificativa: justificativa!,
      },
      {
        onSuccess: (s) => {
          toast.success(
            s.status === "AGUARDANDO_ACEITE"
              ? `Enviado para o aceite de ${person?.nome}`
              : "Pedido enviado ao Planejamento",
          );
          onClose();
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : "Não foi possível abrir a solicitação"),
      },
    );
  };

  const previsualizar = () => {
    if (!projectId) return;
    onPreview({ personId, date, projectId, tipo, unidade, qtd });
  };

  return (
    <Overlay onClose={onClose} title={`Alocar ${person?.nome} · ${fmt(date)}`}>
      {!projectId ? (
        <>
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por OS ou cliente"
              className="h-9 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-72 space-y-1 overflow-auto">
            {lista.map((p) => (
              <button
                key={p.id}
                onClick={() => setProjectId(p.id)}
                className="flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left hover:bg-secondary"
              >
                <span
                  className="h-6 w-1 rounded-sm"
                  style={{
                    background:
                      p.estagio === "Em orçamento"
                        ? params.corOrcamento
                        : getAM(p.coordenadorId ?? "")?.cor,
                  }}
                />
                <span className="flex-1 truncate text-xs text-foreground">{p.os}</span>
                <span className="text-[10px] text-muted-foreground">{p.estagio}</span>
              </button>
            ))}
            {!lista.length && (
              <p className="p-4 text-center text-xs text-muted-foreground">
                Nenhum job encontrado.
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <div className="rounded-md border border-border px-2 py-1.5 text-xs text-foreground">
            {osOf(projectId)}
          </div>

          {roteirista ? (
            <>
              <p className="text-[11px] text-muted-foreground">
                Roteirista é alocado por período de conteúdo (não dia a dia).
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Campo label="Início">
                  <input readOnly value={date} className={inp} />
                </Campo>
                <Campo label="Fim">
                  <input
                    type="date"
                    value={fim}
                    onChange={(e) => setFim(e.target.value)}
                    className={inp}
                  />
                </Campo>
              </div>
              <Campo label="Horas de conteúdo">
                <input
                  type="number"
                  value={qtd}
                  onChange={(e) => setQtd(Number(e.target.value) || 0)}
                  className={inp}
                />
              </Campo>
            </>
          ) : (
            <>
              <Campo label="Tipo do slot">
                <div className="flex flex-wrap gap-1">
                  {params.slotTipos.map((t) => (
                    <button
                      key={t}
                      onClick={() => escolherTipo(t)}
                      className={cn(
                        "rounded-md border px-2 py-1 text-[11px]",
                        tipo === t
                          ? "border-primary bg-primary/15 text-primary"
                          : "border-border text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Campo>

              {tipo === "Alteração" && (
                <div className="rounded-md border-2 border-primary px-2 py-1.5 text-center text-xs font-black tracking-widest text-primary">
                  ALT
                </div>
              )}
              {
                <div className="grid grid-cols-2 gap-2">
                  <Campo label="Unidade">
                    <div className="inline-flex overflow-hidden rounded-md border border-border">
                      {(["slides", "horas"] as const).map((u) => (
                        <button
                          key={u}
                          onClick={() => {
                            setUnidade(u);
                            setQtd(u === "slides" ? params.slidesPorDia : params.horasPorDia);
                          }}
                          className={cn(
                            "px-2 py-1 text-[11px]",
                            unidade === u
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </Campo>
                  <Campo label={unidade === "slides" ? "Slides" : "Horas"}>
                    <input
                      type="number"
                      value={qtd}
                      onChange={(e) => setQtd(Number(e.target.value) || 0)}
                      className={inp}
                    />
                  </Campo>
                </div>
              }
              {tipo === "Alteração" && (
                <p className="text-[10px] text-muted-foreground">
                  Alteração nasce em horas — troque a unidade só se este slot for medido em slides.
                </p>
              )}
              {(tipo === "IV" || tipo === "Adaptação de IV") && (
                <p className="text-[10px] text-muted-foreground">
                  Padrão dia inteiro com {params.slidesIVPorDia} slides — pode ser alocado em mais
                  de um dia.
                </p>
              )}
            </>
          )}

          {justificativa !== null && (
            <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2">
              <p className="text-[11px] text-foreground">
                {person?.nome} ainda não está neste projeto. Assumir trabalho novo passa pelo
                Planejamento e pelo aceite de quem vai executar.
              </p>
              <textarea
                autoFocus
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={2}
                placeholder="Justificativa (obrigatória)"
                className={cn(inp, "resize-none")}
              />
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setProjectId(null)}
            >
              Voltar
            </Button>
            {!roteirista && justificativa === null && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 gap-1 text-xs font-semibold"
                onClick={previsualizar}
              >
                <Eye className="h-3.5 w-3.5" /> Pré-visualizar impacto
              </Button>
            )}
            {justificativa === null ? (
              <Button size="sm" className="h-8 text-xs" onClick={salvar}>
                Alocar
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={criarSolicitacao.isPending}
                onClick={pedirAlocacao}
              >
                Abrir solicitação
              </Button>
            )}
          </div>
          {!roteirista && (
            <p className="rounded-md border border-dashed border-border bg-muted/40 px-2 py-1.5 text-[10px] text-muted-foreground">
              <strong className="text-foreground">Pré-visualizar impacto</strong> desenha o slot em
              tracejado na grade e abre o painel com horas na semana, projetos simultâneos e
              conflitos antes de confirmar a alocação.
            </p>
          )}
        </div>
      )}
    </Overlay>
  );
}

/* ---------- Detalhe do slot ---------- */
function DetalheSlot({ slot, onClose }: { slot: DayAlloc; onClose: () => void }) {
  const params = useParams();
  const [qtd, setQtd] = useState(slot.qtd);
  const [tipo, setTipo] = useState(slot.tipo);
  const [unidade, setUnidade] = useState<"slides" | "horas">(slot.unidade);
  const [pedido, setPedido] = useState<"Trocar" | "Retirar" | "Adicionar" | null>(null);
  const [just, setJust] = useState("");
  const [proposto, setProposto] = useState(slot.personId);
  const roster = usePeople();
  const criarSolicitacao = useCriarSolicitacao();

  const enviarPedido = () => {
    if (!pedido) return;
    if (just.trim().length < 10) {
      toast.error("Descreva o porquê — o Planejamento decide com base nisso");
      return;
    }
    const tipo = pedido === "Trocar" ? "TROCAR" : pedido === "Retirar" ? "RETIRAR" : "ADICIONAR";
    criarSolicitacao.mutate(
      {
        tipo,
        projetoId: slot.projectId,
        // Em Trocar, quem entra; nos demais, quem já está no slot.
        profissionalId: tipo === "TROCAR" ? proposto : slot.personId,
        inicio: slot.date,
        alocacaoDiaId: slot.id,
        ...(tipo !== "RETIRAR" && {
          tipoSlot: slot.tipo,
          unidade: slot.unidade === "horas" ? ("HORAS" as const) : ("SLIDES" as const),
          quantidade: slot.qtd,
        }),
        justificativa: just,
      },
      {
        onSuccess: (s) => {
          toast.success(
            s.status === "AGUARDANDO_ACEITE"
              ? "Aprovado direto — agora aguarda o aceite do profissional"
              : "Pedido enviado ao Planejamento",
          );
          onClose();
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : "Não foi possível enviar o pedido"),
      },
    );
  };

  return (
    <Overlay onClose={onClose} title={osOf(slot.projectId)}>
      <div className="space-y-3">
        <div className="text-[11px] text-muted-foreground">
          {fmt(slot.date)} · {slot.tipo}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Campo label="Tipo">
            <select
              value={tipo}
              onChange={(e) => {
                const t = e.target.value;
                setTipo(t);
                setUnidade(unidadeDoTipo(t));
              }}
              className={inp}
            >
              {params.slotTipos.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Unidade">
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {(["slides", "horas"] as const).map((u) => (
                <button
                  key={u}
                  onClick={() => setUnidade(u)}
                  className={cn(
                    "px-2 py-1 text-[11px]",
                    unidade === u ? "bg-primary text-primary-foreground" : "text-muted-foreground",
                  )}
                >
                  {u}
                </button>
              ))}
            </div>
          </Campo>
        </div>
        <Campo label={unidade === "slides" ? "Slides previstos" : "Horas previstas"}>
          <input
            type="number"
            value={qtd}
            onChange={(e) => setQtd(Number(e.target.value) || 0)}
            className={inp}
          />
        </Campo>

        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-8 flex-1 text-xs"
            onClick={() => {
              updateDayAlloc(slot.id, { qtd, tipo, unidade });
              toast.success("Slot atualizado");
              onClose();
            }}
          >
            Salvar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1 text-xs text-health-red"
            onClick={() => {
              removeDayAlloc(slot.id);
              onClose();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remover
          </Button>
        </div>

        <div className="rounded-md border border-border p-2">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Pedido de alteração ao Planejamento
          </div>
          <div className="flex gap-1">
            {(
              [
                ["Trocar", ArrowLeftRight],
                ["Retirar", MinusCircle],
                ["Adicionar", Plus],
              ] as const
            ).map(([t, Icon]) => (
              <button
                key={t}
                onClick={() => setPedido(t)}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-[11px]",
                  pedido === t
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" /> {t}
              </button>
            ))}
          </div>
          {pedido && (
            <div className="mt-2 space-y-2">
              {pedido === "Trocar" && (
                <Campo label="Quem entra no lugar">
                  <select
                    value={proposto}
                    onChange={(e) => setProposto(e.target.value)}
                    className={inp}
                  >
                    {roster.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </Campo>
              )}
              <textarea
                value={just}
                onChange={(e) => setJust(e.target.value)}
                rows={2}
                placeholder="Justificativa (obrigatória)"
                className={cn(inp, "resize-none")}
              />
              <Button
                size="sm"
                className="h-8 w-full text-xs"
                disabled={criarSolicitacao.isPending}
                onClick={enviarPedido}
              >
                Enviar pedido
              </Button>
              <p className="text-[10px] text-muted-foreground">
                Nada muda na grade até o Planejamento decidir e o profissional aceitar.
              </p>
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
}

/* ---------- Bloqueio ---------- */
function BloqueioModal({
  person,
  podeGerir,
  onClose,
}: {
  person: Person;
  podeGerir: boolean;
  onClose: () => void;
}) {
  const params = useParams();
  const solicitar = useSolicitarBloqueio();
  const externo = person.tipo === "Freelancer";
  const hoje = new Date().toISOString().slice(0, 10);
  const [inicio, setInicio] = useState(hoje);
  const [fim, setFim] = useState(hoje);
  const [tipo, setTipo] = useState(params.bloqueioTipos[0] ?? "Férias");
  const [motivo, setMotivo] = useState("");

  const antecedencia = daysBetween(hoje, inicio);
  const foraJanela = antecedencia < params.janelaBloqueioDias;

  const enviar = () => {
    if (externo && !motivo.trim()) {
      toast.error("Motivo é obrigatório para externos");
      return;
    }
    solicitar.mutate(
      {
        profissionalId: person.id,
        inicio,
        fim,
        tipo: externo ? undefined : tipo,
        motivo: motivo.trim() || undefined,
      },
      {
        onSuccess: (r) => {
          toast.success(
            r.bloqueio.status === "APROVADO"
              ? "Bloqueio registrado na agenda"
              : "Solicitação enviada para aprovação",
            {
              description: r.foraDaJanela
                ? `Menos de ${r.janelaDias} dias de antecedência — o Planejamento decide.`
                : undefined,
            },
          );
          onClose();
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : "Falha ao registrar o bloqueio"),
      },
    );
  };

  return (
    <Overlay onClose={onClose} title={`Bloquear agenda · ${person.nome}`}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Início">
            <input
              type="date"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
              className={inp}
            />
          </Campo>
          <Campo label="Fim">
            <input
              type="date"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
              className={inp}
            />
          </Campo>
        </div>
        {!externo ? (
          <Campo label="Tipo de bloqueio">
            <div className="flex flex-wrap gap-1">
              {params.bloqueioTipos.map((t) => (
                <button
                  key={t}
                  onClick={() => setTipo(t)}
                  className={cn(
                    "rounded-md border px-2 py-1 text-[11px]",
                    tipo === t
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </Campo>
        ) : (
          <Campo label="Motivo (obrigatório)">
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className={inp}
              placeholder="Descreva o motivo"
            />
          </Campo>
        )}
        {foraJanela && !podeGerir && (
          <p className="rounded-md border border-health-yellow/50 bg-health-yellow/10 px-2 py-1.5 text-[11px] text-health-yellow">
            Fora da janela de antecedência ({params.janelaBloqueioDias} dias) — depende de aprovação
            do gestor.
          </p>
        )}
        <p className="text-[10px] text-muted-foreground">
          Nenhum bloqueio é aprovado automaticamente; toda solicitação sobe ao gestor.
        </p>
        <Button size="sm" className="h-8 w-full text-xs" onClick={enviar}>
          {podeGerir ? "Bloquear" : "Solicitar bloqueio"}
        </Button>
      </div>
    </Overlay>
  );
}

/* ---------- utilitários visuais ---------- */
const inp =
  "w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring";

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function Overlay({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-xl border border-border bg-card p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
